import React from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { NavLink } from 'react-router-dom'

import { sizes } from '@qflow/theme'
import { useResponsive } from 'portal/lib/hooks'
import { Spacer } from 'portal/lib/primitives/layout'
import * as ButtonBase from 'portal/lib/primitives/buttons/ButtonBase'
import { SIDEBAR_HORIZONTAL_PADDING } from './constants'

export function NavButton({
  id = 'Nav',
  children,
  onClick,
  to,
  Icon,
  disabled,
  btnTag = '',
  noWrapper = false,
  UpgradeToProComponent = null,
  minimalMargin = false,
  style = null
}) {
  const { isMobile } = useResponsive()

  if (disabled && UpgradeToProComponent) {
    return (
      <DisabledNavLink>
        <div>
          <SummaryLeft>
            {Icon && (
              <Icon size={sizes.MEDIUM} colour="currentColor" style={style} />
            )}
            {children}
            {btnTag !== '' && <TagDiv>{btnTag}</TagDiv>}
          </SummaryLeft>

          <UpgradeToProComponent feature={id} />
        </div>
      </DisabledNavLink>
    )
  }

  return (
    <StyledNavLink
      id={id + 'SideBarButton'}
      to={to}
      onClick={onClick}
      disabled={disabled}
      children={({ isActive }) => (
        <>
          {disabled || noWrapper || <ActiveBar />}

          <ButtonBody
            disabled={isActive}
            optionUnavailable={disabled}
            mobile={isMobile}
            $noWrapper={noWrapper}
            $minimalMargin={minimalMargin}
          >
            {Icon && (
              <>
                <Icon size={sizes.MEDIUM} colour="currentColor" style={style} />
                <Spacer.Small />
              </>
            )}
            {children}
            {btnTag !== '' && (
              <>
                <Spacer.Large />
                <Spacer.Small /> <TagDiv>{btnTag}</TagDiv>
                {disabled && UpgradeToProComponent && <UpgradeToProComponent />}
              </>
            )}
          </ButtonBody>
        </>
      )}
    />
  )
}

NavButton.propTypes = {
  id: PropTypes.string,
  children: PropTypes.node.isRequired,
  to: PropTypes.string,
  onClick: PropTypes.func,
  Icon: PropTypes.elementType,
  disabled: PropTypes.bool,
  btnTag: PropTypes.string
}

const StyledNavLink = styled(NavLink)`
  display: flex;
  flex-direction: row;
  position: relative;
  align-items: center;
  justify-content: space-between;

  width: calc(100% + ${SIDEBAR_HORIZONTAL_PADDING});
  text-decoration: none;

  margin-left: -${SIDEBAR_HORIZONTAL_PADDING};
  margin-top: 0;
  padding: 1px 0;

  &.active [class*='NavButton__ActiveBar'] {
    background-color: ${({ theme }) => theme.colours.TEAL} !important;
  }
`
const ButtonBody = styled.button`
  ${ButtonBase.BaseCSS}
  ${ButtonBase.getColoursCSS({
    getBg: () => 'transparent',
    getBgActive: ({ theme }) => theme.sidebar.navButtonBgActive,
    getBgDisabled: ({ theme }) => theme.sidebar.navButtonBgActive,
    getBgHover: () => 'transparent',
    getFg: ({ theme }) => theme.sidebar.navButtonFg,
    getFgHover: ({ theme }) => theme.sidebar.navButtonFgActive,
    getFgActive: ({ theme }) => theme.sidebar.navButtonFgActive,
    getFgDisabled: ({ theme }) => theme.colours.TEAL
  })}
  ${ButtonBase.getShapeCSS({
    getHeight: () => '2rem'
  })}

  ${({ optionUnavailable, theme }) =>
    optionUnavailable &&
    css`
      background-color: 'transparent' !important;
      pointer-events: none;
    `}

  width: ${({ mobile }) => (mobile ? '176px' : '196px')};
  justify-content: flex-start;

  margin: ${({ $noWrapper, $minimalMargin }) =>
    $noWrapper ? ($minimalMargin ? '0 2px' : '6px 2px 6px 2px') : '0 0 0 33px'};
  padding-left: 12px;

  font-size: ${sizes.SMALL_MEDIUM};
`
const ActiveBar = styled.div`
  position: absolute;

  background-color: transparent;
  top: 0;
  bottom: 0;
  left: 17px;
  width: 1px;

  transition: background-color 100ms ease-in-out;

  background-color: rgba(255, 255, 255, 0.25);
`
const TagDiv = styled.div`
  border-style: ridge;
  border-radius: 4px;
  padding: 17ox;
  padding-right: 7px;
  padding-left: 7px;
  font-weight: bolder;
`
const DisabledNavLink = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  list-style: none;
  font-size: 14px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.5);
  outline: none;
  user-select: none;
  padding: 0.5rem 6px 0.5rem 6px;

  & > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.25rem 0;
  }
`
const SummaryLeft = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
`
